import { Box, MenuItem, Select, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import type React from 'react';
import { useMemo, useState } from 'react';
import { transformData } from '/@/components/crop-monitoring/assets/graphicData';
import Graphic from '/@/components/crop-monitoring/graphic';
import Icon from '/@/components/icon';
import LineGraphic from '/@/components/line-graphic';
import Tabs from '/@/components/new-tabs';
import colors from '/@/components/theme-provider/colors';
import { _ } from '/@/locale';
import useIsMobile from '/@/utils/hooks/is-mobile';
import CropMonitoringDetailCard from '../../detail-card';
import { References } from '../../gdu-graphic';

const PageArrow = ({ direction, onClick, page, pages }) => {
    const disabled = (direction === 'left' && page === 1) || (direction === 'right' && page === pages);
    return (
        <Box
            sx={[
                {
                    '& i': {
                        mb: 0.25,
                        borderRadius: '100%',
                        background: colors.gray[800],
                        color: 'white',
                    },
                },
                !disabled && {
                    '& i': {
                        cursor: 'pointer',
                        '&:hover': {
                            background: (t) => t.palette.primary.main,
                        },
                    },
                },
                disabled && {
                    '& i': {
                        background: colors.gray[400],
                    },
                },
                direction === 'right' && {
                    mr: 1,
                },
                direction === 'left' && {
                    ml: 1,
                },
            ]}
            onClick={() => !disabled && onClick()}
        >
            <i className='material-icons'>{`chevron_${direction}`}</i>
        </Box>
    );
};

type WeatherTab = 'temperature' | 'rain' | 'wind' | 'humidity';

interface WeatherCardConfig {
    title: string;
    quantity: number;
    icon: JSX.Element;
    showYAxis?: boolean;
    showXAxis: boolean;
    text?: string;
    domain: number[];
    graphicType?: 'line' | 'bar';
    max?: number | null;
    min?: number;
    references?: Array<{
        label: string;
        color: string;
        dashed?: boolean;
    }>;
}

interface GraphicCardsProps {
    hideTitle?: boolean;
    hideBorders?: boolean;
    historicalWeather: unknown;
    weatherCardsConfiguration: Record<WeatherTab, WeatherCardConfig>;
}

const GraphicCards: React.FC<GraphicCardsProps> = ({
    weatherCardsConfiguration,
    historicalWeather,
    hideTitle = false,
    hideBorders = false,
}) => {
    const [selectedWeatherTab, setSelectedWeatherTab] = useState<WeatherTab>('rain');

    const styles = {
        fillActive: {
            '& svg': { fill: (t) => t.palette.secondary.main },
        },
        fillInactive: {
            '& svg': { fill: (t) => t.palette.primary.mui.inactive },
        },
        container: {
            border: { xs: 1, md: 0 },
            borderRadius: 4,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            borderColor: (t) => t.palette.divider,
            background: { md: 'white' },
            boxShadow: { md: 'rgb(100 100 111 / 20%) 0px 7px 29px 0px' },
        },
    };

    const tabConfig = [
        { value: 'rain', icon: <Icon type='rain' />, label: _('Precipitaciones') },
        { value: 'temperature', icon: <Icon type='sun' />, label: _('Temperatura') },
        { value: 'wind', icon: <Icon type='air' />, label: _('Viento') },
        { value: 'humidity', icon: <Icon type='humidity2' />, label: _('Humedad') },
    ];

    const theme = useTheme();
    const isLowerThanMd = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <CropMonitoringDetailCard title={_('Datos meteorológicos')} hideTitle={hideTitle} hideBorders={hideBorders}>
            <Stack sx={{ width: '100%' }} data-testid='graphic-cards-stack'>
                {isLowerThanMd ? (
                    <Select
                        onChange={(e) => setSelectedWeatherTab(e.target.value)}
                        value={selectedWeatherTab}
                        sx={{
                            borderRadius: '0 !important',
                            '.MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                p: 0,
                            },
                            '.MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                                borderBottom: (t) => `2px solid ${t.palette.secondary.main} !important`,
                            },
                        }}
                    >
                        {tabConfig.map((tab) => (
                            <MenuItem
                                key={tab.value}
                                value={tab.value}
                                selected={selectedWeatherTab === tab.value}
                                sx={{
                                    p: 0,
                                    m: 0,
                                }}
                            >
                                <Tab
                                    disableRipple
                                    key={tab.value}
                                    icon={
                                        <Box
                                            sx={
                                                selectedWeatherTab === tab.value
                                                    ? styles.fillActive
                                                    : styles.fillInactive
                                            }
                                        >
                                            {tab.icon}
                                        </Box>
                                    }
                                    iconPosition='start'
                                    value={tab.value}
                                    label={tab.label}
                                    selected={selectedWeatherTab === tab.value}
                                    sx={{
                                        color: (t) =>
                                            selectedWeatherTab === tab.value ? t.palette.secondary.main : undefined,
                                    }}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                ) : (
                    <Tabs
                        textColor='secondary'
                        indicatorColor='secondary'
                        value={selectedWeatherTab}
                        variant={isLowerThanMd ? 'scrollable' : 'fullWidth'}
                        onChange={(_, value: WeatherTab) => setSelectedWeatherTab(value)}
                        sx={{
                            borderTop: 'none',
                        }}
                    >
                        {tabConfig.map((tab) => (
                            <Tab
                                key={tab.value}
                                icon={
                                    <Box
                                        sx={selectedWeatherTab === tab.value ? styles.fillActive : styles.fillInactive}
                                    >
                                        {tab.icon}
                                    </Box>
                                }
                                iconPosition='start'
                                value={tab.value}
                                label={tab.label}
                            />
                        ))}
                    </Tabs>
                )}
            </Stack>

            <CardGraphic
                {...weatherCardsConfiguration[selectedWeatherTab]}
                type={selectedWeatherTab}
                historicalWeather={historicalWeather}
            />
        </CropMonitoringDetailCard>
    );
};

const CardGraphic = ({
    historicalWeather,
    type,
    quantity,
    title,
    icon,
    showXAxis,
    showYAxis,
    domain,
    max,
    graphicType,
    text,
    min,
    references,
}: {
    historicalWeather: unknown;
    title: string;
    type: 'temperature' | 'rain' | 'wind' | 'humidity';
    quantity: number;
    icon: JSX.Element;
    showYAxis?: boolean;
    showXAxis: boolean;
    text?: string;
    domain: number[];
    graphicType?: 'line' | 'bar';
    max?: number | null;
    min?: number;
    references?: Array<{
        label: string;
        color: string;
        dashed?: boolean;
    }>;
}) => {
    const isMobile = useIsMobile();
    const pages = Math.ceil(historicalWeather.count / quantity);

    const [page, setPage] = useState(pages);

    const filteredData = useMemo(() => {
        if (historicalWeather && quantity && pages)
            return transformData(historicalWeather, type, quantity, page, pages);
    }, [historicalWeather, type, quantity, page, pages]);

    const previousPage = () => {
        setPage((pv) => pv - 1);
    };

    const nextPage = () => {
        setPage((pv) => pv + 1);
    };

    return (
        <Grid
            container
            alignItems='center'
            justifyContent={!isMobile ? 'flex start' : 'center'}
            sx={{
                ...(isMobile
                    ? { pt: { xs: 2, sm: 2 }, pb: { xs: 0, sm: 2 }, pl: { xs: 0, sm: 2 }, pr: { xs: 0, sm: 2 } }
                    : { p: 3 }),
            }}
        >
            <Typography variant='caption' color='primary.mui.inactive' fontWeight='bold'>
                {title}
            </Typography>
            <Grid
                item
                sx={{
                    height: isMobile ? '250px' : '350px',
                    width: '100%',
                    my: 2,
                    position: 'relative',
                    mt: { md: 2 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                    }}
                >
                    <PageArrow direction='left' onClick={() => previousPage()} page={page} pages={pages} />
                    {graphicType === 'line' ? (
                        <LineGraphic
                            dataGraphic={filteredData || []}
                            type={type}
                            width={'100%'}
                            height={isMobile ? 250 : 350}
                            showXAxis={showXAxis}
                            showYAxis={showYAxis}
                            domain={domain}
                            max={max}
                            min={min}
                        />
                    ) : (
                        <Graphic
                            dataGraphic={filteredData || []}
                            type={type}
                            width={'100%'}
                            height={isMobile ? 250 : 350}
                            showXAxis={showXAxis}
                            showYAxis={showYAxis}
                            domain={domain}
                            max={max}
                        />
                    )}

                    <PageArrow direction='right' onClick={() => nextPage()} page={page} pages={pages} />
                </Box>
            </Grid>

            {references && <References references={references} />}
        </Grid>
    );
};

export default GraphicCards;
